import * as XLSX from 'xlsx';
import type { PlanBudgetSection } from './ndis-plan-model';

export interface CatalogueItem {
	supportItemNumber: string;
	supportItemName: string;
	registrationGroup: string;
	supportCategory: string;
	unit: string;
	quoteRequired: boolean;
	nonFaceToFace: boolean;
	providerTravel: boolean;
	shortNoticeCancellation: boolean;
	priceLimit: string;
	remotePriceLimit: string;
	veryRemotePriceLimit: string;
}

export type MatchConfidence = 'high' | 'medium' | 'low';
export type MatchStatus = 'possible' | 'selected' | 'rejected' | 'needs_review';

export interface CatalogueMatch {
	catalogueItem: CatalogueItem;
	matchReason: string;
	confidence: MatchConfidence;
	status: MatchStatus;
	notes: string;
}

export interface BudgetCatalogueResult {
	budgetId: string;
	budgetCategory: string;
	budgetAmount: string;
	matches: CatalogueMatch[];
}

type CatalogueColumn = keyof CatalogueItem;

type ColumnMatch = {
	column: CatalogueColumn;
	predicate: (header: string) => boolean;
};

const COLUMN_MATCHES: ColumnMatch[] = [
	{
		column: 'supportItemNumber',
		predicate: (header) => header.includes('support item number') || header.includes('item number') || header.includes('item no'),
	},
	{
		column: 'supportItemName',
		predicate: (header) => header.includes('support item name') || header.includes('item name') || header.includes('description'),
	},
	{
		column: 'registrationGroup',
		predicate: (header) => header.includes('registration group') || header.includes('reg group'),
	},
	{
		column: 'supportCategory',
		predicate: (header) => header.includes('support category') || header === 'category' || header.includes(' category'),
	},
	{
		column: 'unit',
		predicate: (header) => header === 'unit' || header.includes('unit of measure'),
	},
	{
		column: 'quoteRequired',
		predicate: (header) => header.includes('quote'),
	},
	{
		column: 'nonFaceToFace',
		predicate: (header) => header.includes('non-face') || header.includes('non face'),
	},
	{
		column: 'providerTravel',
		predicate: (header) => header.includes('provider travel') || header === 'travel' || header.includes(' travel'),
	},
	{
		column: 'shortNoticeCancellation',
		predicate: (header) => header.includes('short notice') || header.includes('cancellation'),
	},
	{
		column: 'priceLimit',
		predicate: (header) => (header.includes('price limit') || header.includes('price')) && !header.includes('remote'),
	},
	{
		column: 'remotePriceLimit',
		predicate: (header) => header.includes('remote') && !header.includes('very remote'),
	},
	{
		column: 'veryRemotePriceLimit',
		predicate: (header) => header.includes('very remote'),
	},
];

const CATEGORY_MAP: ReadonlyArray<{ labels: string[]; terms: string[] }> = [
	{ labels: ['core_supports', 'core_daily_life', 'core supports', 'core'], terms: ['core', 'daily life'] },
	{ labels: ['capacity_building', 'capacity building', 'capacity building supports'], terms: ['capacity building'] },
	{ labels: ['capital_supports', 'capital supports', 'capital'], terms: ['capital'] },
	{ labels: ['transport'], terms: ['transport'] },
	{ labels: ['consumables'], terms: ['consumables'] },
	{ labels: ['assistive_technology', 'assistive technology'], terms: ['assistive technology'] },
	{ labels: ['home_modifications', 'home modifications'], terms: ['home modification'] },
	{ labels: ['support_coordination', 'coordination of supports', 'support coordination'], terms: ['support coordination', 'coordination'] },
	{ labels: ['improved_daily_living', 'improved daily living'], terms: ['daily living', 'daily activities'] },
	{ labels: ['improved_relationships', 'improved relationships'], terms: ['relationships', 'behaviour'] },
	{
		labels: ['social_community', 'increased social and community participation', 'social community and civic'],
		terms: ['social', 'community participation'],
	},
];

const CONFIDENCE_ORDER: Record<MatchConfidence, number> = {
	high: 0,
	medium: 1,
	low: 2,
};

function normalise(value: string): string {
	return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function cellToString(value: unknown): string {
	if (value === null || value === undefined) return '';
	if (value instanceof Date) return value.toISOString().slice(0, 10);
	return String(value).trim();
}

function cellToBoolean(value: unknown): boolean {
	const text = normalise(cellToString(value));
	return text === 'y' || text === 'yes' || text === 'true' || text === '1';
}

function detectColumns(headerRow: readonly unknown[]): Map<CatalogueColumn, number> {
	const detected = new Map<CatalogueColumn, number>();

	headerRow.forEach((rawHeader, index) => {
		const header = normalise(cellToString(rawHeader));
		if (!header) return;

		for (const match of COLUMN_MATCHES) {
			if (!detected.has(match.column) && match.predicate(header)) {
				detected.set(match.column, index);
				break;
			}
		}
	});

	return detected;
}

function rowValue(row: readonly unknown[], columns: Map<CatalogueColumn, number>, column: CatalogueColumn): unknown {
	const index = columns.get(column);
	return index === undefined ? '' : row[index];
}

function createCatalogueItem(row: readonly unknown[], columns: Map<CatalogueColumn, number>): CatalogueItem {
	return {
		supportItemNumber: cellToString(rowValue(row, columns, 'supportItemNumber')),
		supportItemName: cellToString(rowValue(row, columns, 'supportItemName')),
		registrationGroup: cellToString(rowValue(row, columns, 'registrationGroup')),
		supportCategory: cellToString(rowValue(row, columns, 'supportCategory')),
		unit: cellToString(rowValue(row, columns, 'unit')),
		quoteRequired: cellToBoolean(rowValue(row, columns, 'quoteRequired')),
		nonFaceToFace: cellToBoolean(rowValue(row, columns, 'nonFaceToFace')),
		providerTravel: cellToBoolean(rowValue(row, columns, 'providerTravel')),
		shortNoticeCancellation: cellToBoolean(rowValue(row, columns, 'shortNoticeCancellation')),
		priceLimit: cellToString(rowValue(row, columns, 'priceLimit')),
		remotePriceLimit: cellToString(rowValue(row, columns, 'remotePriceLimit')),
		veryRemotePriceLimit: cellToString(rowValue(row, columns, 'veryRemotePriceLimit')),
	};
}

export function parseCatalogueXlsx(arrayBuffer: ArrayBuffer): CatalogueItem[] {
	const workbook = XLSX.read(arrayBuffer, { type: 'array' });
	const preferredSheetName = workbook.SheetNames.find((name) => normalise(name) === 'support catalogue');
	const sheetName = preferredSheetName ?? workbook.SheetNames[0];
	if (!sheetName) return [];

	const sheet = workbook.Sheets[sheetName];
	if (!sheet) return [];

	const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
	const headerRow = rows[0];
	if (!headerRow) return [];

	const columns = detectColumns(headerRow);
	return rows
		.slice(1)
		.map((row) => createCatalogueItem(row, columns))
		.filter((item) => item.supportItemNumber.length > 0);
}

function wordsFromCategory(category: string): string[] {
	return normalise(category)
		.split(/[^a-z0-9]+/)
		.filter((word) => word.length > 2 && word !== 'and' && word !== 'the' && word !== 'with');
}

function mappedTerms(category: string): string[] {
	const normalisedCategory = normalise(category);
	const matches = CATEGORY_MAP.filter((entry) => entry.labels.some((label) => normalisedCategory.includes(label)));
	return matches.flatMap((match) => match.terms);
}

function uniqueTerms(terms: string[]): string[] {
	return [...new Set(terms.map(normalise).filter(Boolean))];
}

function supportCategoryKey(category: string): string {
	return normalise(category).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function matchCatalogueItem(budget: PlanBudgetSection, item: CatalogueItem): CatalogueMatch | null {
	const normalisedCategory = normalise(budget.normalizedCategory);
	const itemCategory = normalise(item.supportCategory);
	const itemCategoryKey = supportCategoryKey(item.supportCategory);
	const itemName = normalise(item.supportItemName);
	const terms = uniqueTerms([
		...mappedTerms(budget.normalizedCategory),
		...mappedTerms(budget.categoryName),
		...wordsFromCategory(budget.normalizedCategory),
		...wordsFromCategory(budget.categoryName),
	]);

	if (normalisedCategory && itemCategoryKey === normalisedCategory) {
		return {
			catalogueItem: item,
			matchReason: `Budget category '${budget.categoryName}' exactly matches catalogue support category '${item.supportCategory}' using normalized category '${budget.normalizedCategory}'.`,
			confidence: 'high',
			status: 'possible',
			notes: '',
		};
	}

	const categoryTerm = terms.find((term) => itemCategory.includes(term));
	if (categoryTerm) {
		return {
			catalogueItem: item,
			matchReason: `Budget category '${budget.categoryName}' matches catalogue support category '${item.supportCategory}' using '${categoryTerm}'.`,
			confidence: 'medium',
			status: 'possible',
			notes: '',
		};
	}

	const nameTerm = terms.find((term) => itemName.includes(term));
	if (nameTerm) {
		return {
			catalogueItem: item,
			matchReason: `Budget category '${budget.categoryName}' partially matches support item name '${item.supportItemName}' using '${nameTerm}'.`,
			confidence: 'low',
			status: 'possible',
			notes: '',
		};
	}

	return null;
}

export function matchBudgetsToCatalogue(
	budgets: PlanBudgetSection[],
	catalogueItems: CatalogueItem[],
): BudgetCatalogueResult[] {
	return budgets.map((budget) => {
		const matches = catalogueItems
			.map((item) => matchCatalogueItem(budget, item))
			.filter((match): match is CatalogueMatch => match !== null)
			.sort((a, b) => {
				const confidenceDifference = CONFIDENCE_ORDER[a.confidence] - CONFIDENCE_ORDER[b.confidence];
				if (confidenceDifference !== 0) return confidenceDifference;
				return a.catalogueItem.supportItemName.localeCompare(b.catalogueItem.supportItemName);
			})
			.slice(0, 20);

		return {
			budgetId: budget.id,
			budgetCategory: budget.categoryName,
			budgetAmount: budget.amount,
			matches,
		};
	});
}
